import { createPortal } from 'react-dom';
import { Users, X, Linkedin, ChevronRight } from 'lucide-react';

import { contactEvidence, TIER_STYLE } from '../Outreach/referralReason';
import {
    avatarBg, initials, contactEmail, HoverTip, EmailCopyButton, referralProb, REFERRAL_PROB_TIP
} from './ContactCard';

/**
 * The full referral list, with the reasoning the sidebar card has no room for.
 *
 * WHY IT IS ITS OWN COMPONENT
 *
 * A job and a public company reach the same contacts through the same endpoint,
 * and the argument for each person — their tier, what makes them worth a
 * message, the caveats, how to open — does not change based on which page asked.
 * Keeping one copy is what stops the two from drifting apart.
 *
 * `jobTitle` is optional: on a company page there is no role yet, and
 * contactEvidence simply drops the title-overlap reasoning when it is absent.
 */
const ContactsModal = ({ open, onClose, contacts = [], companyName, jobTitle = null, onChooseStrategy }) => {
    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => onClose?.()} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E8EE] shrink-0">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-[#EEF1FE] flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#3F57E0]" />
                        </div>
                        <div>
                            <p className="text-[14px] font-bold text-[#0f172a]">Referral contacts</p>
                            <p className="text-[11.5px] text-gray-400">{contacts.length} verified contacts at {companyName}</p>
                        </div>
                    </div>
                    <button onClick={() => onClose?.()}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {/* Contact list */}
                <div className="overflow-y-auto flex flex-col gap-2 px-5 py-4">
                    {contacts.map((c, i) => {
                        const rp = referralProb(c);
                        const photo = c.photo_url || c.profile_picture || c.picture || c.avatar || null;
                        // The modal has room for the full case, so it shows
                        // three supporting reasons instead of the card's two.
                        const ev = contactEvidence(c, jobTitle, 3);
                        const tierStyle = ev.tier ? TIER_STYLE[ev.tier] : null;
                        return (
                            <div key={i} className="flex items-start gap-3 bg-[#F8F9FF] rounded-xl p-3">
                                <span className="w-6 text-right text-[11.5px] font-semibold text-[#94A3B8] tabular-nums shrink-0">{i + 1}.</span>
                                <div className="w-11 h-11 rounded-full shrink-0 overflow-hidden border-2 border-white shadow-sm">
                                    {photo ? <img src={photo} alt={c.name} className="w-full h-full object-cover"
                                                  onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}} /> : null}
                                    <div className="w-full h-full rounded-full flex items-center justify-center text-white text-[13px] font-bold"
                                         style={{ background: `linear-gradient(135deg,${avatarBg(c.name)},${avatarBg(c.name)}cc)`, display: photo?'none':'flex' }}>
                                        {initials(c.name)}
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <p className="text-[13.5px] font-semibold text-[#0f172a] leading-snug">{c.name||'Unknown'}</p>
                                        {tierStyle && (
                                            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-[2px] rounded-full shrink-0"
                                                  style={{ color: tierStyle.color, background: tierStyle.bg }}>
                                                {tierStyle.label}
                                            </span>
                                        )}
                                        {ev.badges.map(b => (
                                            <span key={b} className="text-[9.5px] font-semibold text-[#475569] bg-white border border-[#E6E8EE] rounded px-1.5 py-[1px] shrink-0">{b}</span>
                                        ))}
                                    </div>
                                    <p className="text-[12px] text-gray-400 leading-snug">{c.title||''}</p>
                                    {/* Verdict carries the headline's meaning in a full sentence, so
                                        showing both just says the same thing twice. */}
                                    <p className="text-[11.5px] text-[#334155] leading-snug mt-1">{ev.assessment || ev.headline}</p>
                                    {(ev.reasons.length > 0 || ev.concerns.length > 0) && (
                                        <ul className="mt-1.5 space-y-0.5">
                                            {ev.reasons.map(r => (
                                                <li key={r} className="text-[10.5px] text-[#64748B] leading-snug flex gap-1.5">
                                                    <span className="text-[#CBD5E1] shrink-0">•</span><span>{r}</span>
                                                </li>
                                            ))}
                                            {ev.concerns.map(cn => (
                                                <li key={cn} className="text-[10.5px] text-[#B45309] leading-snug flex gap-1.5">
                                                    <span className="shrink-0">⚠</span><span>{cn}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {/* Collapsed by default so ten contacts stay scannable — native
                                        <details> keeps this state-free inside the map. Open on the
                                        top pick, which is the one most people act on. */}
                                    {ev.outreach.length > 0 && (
                                        <details open={i === 0} className="mt-1.5 group">
                                            <summary className="text-[10px] font-bold uppercase tracking-wide text-[#3F57E0] cursor-pointer list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1 hover:text-[#3347c8]">
                                                <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                                                How to approach them
                                            </summary>
                                            <ul className="mt-1 space-y-0.5 pl-4">
                                                {ev.outreach.map(o => (
                                                    <li key={o} className="text-[10.5px] text-[#475569] leading-snug flex gap-1.5">
                                                        <span className="text-[#CBD5E1] shrink-0">→</span><span>{o}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                                {rp && (
                                    <HoverTip className="shrink-0" tip={REFERRAL_PROB_TIP}>
                                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full cursor-default inline-block"
                                              style={{ color: rp.color, backgroundColor: rp.bg }}>
                                            {rp.prob}%
                                        </span>
                                    </HoverTip>
                                )}
                                <EmailCopyButton email={contactEmail(c)} />
                                {c.profile_url ? (
                                    <a href={c.profile_url} target="_blank" rel="noopener noreferrer"
                                       className="p-1.5 rounded-lg text-gray-400 hover:text-[#0A66C2] transition-colors shrink-0">
                                        <Linkedin className="w-4 h-4" />
                                    </a>
                                ) : (
                                    <HoverTip className="shrink-0" tip={<p className="text-gray-300">No LinkedIn profile found</p>}>
                                        <span className="p-1.5 flex text-gray-300 cursor-default">
                                            <Linkedin className="w-4 h-4" />
                                        </span>
                                    </HoverTip>
                                )}
                            </div>
                        );
                    })}
                </div>
                {/* Footer CTA */}
                <div className="px-5 py-4 border-t border-[#E6E8EE] shrink-0">
                    <button onClick={() => { onClose?.(); onChooseStrategy?.(); }}
                            className="w-full py-2.5 rounded-xl bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[13px] font-semibold transition-colors">
                        Choose your outreach strategy
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ContactsModal;
